import { SyntheticEvent, useEffect, useRef, useMemo, useState } from "react"
import cn from "classnames"

import { TrackSlider } from "modules/PsyProfile/components/AboutMe/components/AudioPlayer/TrackSlider"
import style from "modules/PsyProfile/components/AboutMe/components/AudioPlayer/style.module.scss"
import { usePlayedTimer } from "modules/PsyProfile/components/AboutMe/components/AudioPlayer/usePlayedTimer"
import { useDidMount } from "utils/customHooks"
import { Icon } from "components"
import { IS_DEVELOPMENT } from "utils/env-management"
import { Analytics } from "utils/analytics"
import { Psychologist } from "utils/interfaces"

interface Props {
    className?: string
    src: string
    psy: Psychologist
}

export function AudioPlayer({ src, className = "", psy }: Props) {
    const audioRef = useRef<HTMLMediaElement>(null)
    const buttonRef = useRef<HTMLDivElement>(null)
    const [playState, setPlayState] = useState(false)
    const lastPlayStateRef = useRef(playState)
    const [duration, setDuration] = useState<number>()
    const [currentTime, setCurrentTime] = useState<number>(0)
    const timer = usePlayedTimer(psy)
    const feedbackShownRef = useRef(false)
    const {
        loadedMetaDataHandler,
        timeUpdateHandler,
        play,
        pause,
        handleSliderChange,
        handlePlay,
        handlePause,
    } = useMemo(
        () => ({
            loadedMetaDataHandler(this: void, event: SyntheticEvent<HTMLAudioElement, Event>) {
                if (event.target instanceof HTMLMediaElement && typeof duration === "undefined")
                    setDuration(event.target.duration)
            },
            timeUpdateHandler(this: void, event: SyntheticEvent<HTMLAudioElement, Event>) {
                if (event.target instanceof HTMLMediaElement) {
                    const time = event.target.currentTime
                    setCurrentTime(time)
                    timer.track(time)
                }
            },
            play(this: void) {
                setPlayState(true)
            },
            pause(this: void) {
                setPlayState(false)
            },
            handleSliderChange(this: void, value: number) {
                const audioElement = audioRef.current
                if (audioElement) {
                    audioElement.currentTime = audioElement.duration * value
                }
            },
            handlePlay(this: void, event: SyntheticEvent<HTMLAudioElement, Event>) {
                if (event.target instanceof HTMLMediaElement) {
                    if (!feedbackShownRef.current) {
                        feedbackShownRef.current = true
                    }
                    Analytics.audio_listening_started({ psy })
                }
            },
            handlePause(this: void, event: SyntheticEvent<HTMLAudioElement, Event>) {
                if (event.target instanceof HTMLMediaElement) {
                    const time = event.target.currentTime
                    Analytics.audio_listening_stopped({
                        time: Math.round(time),
                        psy,
                    })
                }
            },
        }),
        []
    )
    useDidMount(() => {
        const audioElement = audioRef.current
        const clickHandler = () => (!lastPlayStateRef.current ? play() : pause())
        /*
         используем addEventListener, потому что play/pause не работает в Safari,
         если вешать обработчик события через react: <div onClick={...}>
        */
        buttonRef.current?.addEventListener("click", clickHandler)
        if (audioElement && audioElement.readyState > 0) {
            setDuration(audioElement.duration)
        }
        return () => buttonRef.current?.removeEventListener("click", clickHandler)
    })

    useEffect(() => {
        const audioElement = audioRef.current
        playState ? audioElement?.play() : audioElement?.pause()
        lastPlayStateRef.current = playState
    }, [playState])

    return (
        <figure className={cn(style.container, className)}>
            <audio
                src={IS_DEVELOPMENT ? `//localhost:8000${src}` : src}
                preload="metadata"
                ref={audioRef}
                onLoadedMetadata={
                    typeof duration === "undefined" ? loadedMetaDataHandler : undefined
                }
                onTimeUpdate={timeUpdateHandler}
                onPlay={handlePlay}
                onPause={handlePause}
            />
            <div className={style.playerControls}>
                <div className={style.controlButton} ref={buttonRef}>
                    <Icon
                        type={playState ? "pause-circle-filled" : "play-circle-filled"}
                        button
                        size={48}
                    />
                </div>
                <TrackSlider
                    onChange={handleSliderChange}
                    current={currentTime}
                    maxDuration={duration}
                />
            </div>
        </figure>
    )
}
